import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { MetricCard } from './MetricCard';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PsychologistMetrics } from '../../../../hooks/api/psychologist/metrics';
import { fetchInactivityRate, fetchInactivityDetail, rateFromDetail, InactivityRateData, InactivityDetailData } from '../../../../hooks/api/psychologist/inactivity';
import { fetchSnapshot, SnapshotData } from '../../../../hooks/api/psychologist/snapshot';
import { useAuth } from '@/contexts/AuthContext';
import { profilePath } from '@/routes/slugs';
import { usePsychologistContext } from '@/contexts/PsychologistContext';
import { useNavigate } from 'react-router-dom';
import apiCoreAdapter from '../../../../hooks/apiCoreAdapter';
import { PENDING_DISCHARGED_STATUS_ID } from '@/utils/psychologistStatus';
import { useContactChannel } from '@/hooks/useContactChannel';
import { useMetricsPanelUrl } from '@/hooks/useMetricsPanelUrl';

interface Blockade {
  active: boolean;
  reason: { name: string } | null;
}

interface Props {
  metrics: PsychologistMetrics | null;
  loading: boolean;
  onGoToTasaInactividad: () => void;
  blockades: Blockade[];
}

type Variant = 'A' | 'B' | 'C1' | 'C2' | 'C3';
type PanelType = 'hub' | 'concept' | 'blocks' | 'cupo' | 'horarios' | 'reprogramacion' | 'inactividad' | null;

const TOPE_BLOCKADE_NAME = 'Tope de Pacientes solicitado por psicologo';
// Bloqueos internos que no puede resolver la psicóloga — se ocultan del mensaje accionable
const LIMITE_PERIODO_BLOCKADE_NAME = 'Límite de pacientes dentro del período';
const DEMASIADOS_PACIENTES_BLOCKADE_NAME = 'Demasiados pacientes';
const INTERNAL_BLOCKADE_NAMES = [LIMITE_PERIODO_BLOCKADE_NAME, DEMASIADOS_PACIENTES_BLOCKADE_NAME];

function getVariant(cupo: number, processLimit: number): Variant | null {
  if (!processLimit) return null;
  if (cupo <= processLimit) return 'A';
  const brecha = ((cupo - processLimit) / processLimit) * 100;
  if (brecha <= 30) return 'B';
  if (brecha <= 100) return 'C1';
  if (brecha <= 400) return 'C2';
  return 'C3';
}

// --- Static data ---

const VARIANT_BASE_STYLES: Record<
  Variant,
  { bg: string; border: string; dot: string; title: string }
> = {
  A: {
    bg: 'bg-[#ECFFD5]',
    border: 'border border-[#86EFAC]',
    dot: '🟢',
    title: '¡Estás en condiciones de recibir pacientes!',
  },
  B: {
    bg: 'bg-[#FFF8DC]',
    border: 'border border-[#FCD34D]',
    dot: '🟡',
    title: 'Tu adherencia necesita mejorar para seguir recibiendo pacientes',
  },
  C1: {
    bg: 'bg-[#FFDEDE]',
    border: 'border border-[#FCA5A5]',
    dot: '🔴',
    title: 'No recibirás pacientes nuevos hasta que tu adherencia mejore',
  },
  C2: {
    bg: 'bg-[#FFDEDE]',
    border: 'border border-[#FCA5A5]',
    dot: '🔴',
    title: 'No recibirás pacientes nuevos hasta que tu adherencia mejore',
  },
  C3: {
    bg: 'bg-[#FFDEDE]',
    border: 'border border-[#FCA5A5]',
    dot: '🔴',
    title: 'No recibirás pacientes nuevos hasta que tu adherencia mejore',
  },
};

const PROCESS_STATES = [
  {
    label: 'Nuevo',
    color: 'bg-[#CCFBF1] text-[#0F766E]',
    description: 'El paciente fue asignado a vos pero aún no tuvo su primera sesión.',
  },
  {
    label: 'Activo',
    color: 'bg-[#DCFCE7] text-[#15803D]',
    description: 'Tuvo sesión en los últimos 30 días.',
  },
  {
    label: 'Inactivo',
    color: 'bg-[#FFE4E6] text-[#BE123C]',
    description: 'Pasaron 30 días desde la última sesión.',
  },
];

const BLOCK_TYPES = [
  {
    name: 'No cuenta con horarios de reprogramación',
    description: 'Si necesitás habilitar más horarios en tu agenda.',
  },
  {
    name: 'Demasiados pacientes',
    description: 'Si llegaste al límite de procesos permitidos.',
  },
  {
    name: 'Límite de pacientes dentro de un período',
    description:
      'Si se te asignaron muchos procesos en poco tiempo (específicamente 5 en un período de 30 días).',
  },
  {
    name: 'Tope de pacientes (solicitado por psico)',
    description:
      'Si vos nos pediste explícitamente atender a una menor cantidad de procesos que nuestro máximo, y llegaste a ese número.',
  },
  {
    name: 'Situaciones personales extraordinarias',
    description:
      'Si nos comentaste que estabas atravesando una situación personal y que por eso necesitabas una pausa.',
  },
  {
    name: 'Vacaciones y días off',
    description: 'Si estás en un período importante de días off.',
  },
];

// Criterios sin la tasa de inactividad (ya explicada arriba)
const MATCH_CRITERIA = [
  'Que trabajés la temática que el paciente busca',
  'Que tu agenda tenga horarios disponibles compatibles con el paciente',
  'Que compartas las preferencias que indicó el paciente (rango etáreo, género, corriente terapéutica, especialización, etc)',
];

// --- Shared panel component ---

interface InfoPanelProps {
  type: PanelType;
  onClose: () => void;
  onSwitch: (type: PanelType) => void;
  onGoToTasaInactividad: () => void;
}

type SubPanelType = 'concept' | 'blocks' | 'cupo' | 'horarios' | 'reprogramacion' | 'inactividad';

const PANEL_TITLES: Record<SubPanelType, string> = {
  concept: '¿Qué es un proceso terapéutico?',
  blocks: 'Tipos de bloqueo',
  cupo: 'Cupo y límite de asignaciones',
  horarios: 'Horarios disponibles',
  reprogramacion: 'Horas de reprogramación',
  inactividad: 'Tasa de inactividad',
};

const PANEL_DESCRIPTIONS: Record<SubPanelType, string> = {
  concept: 'Qué es un proceso terapéutico y los estados posibles.',
  blocks: 'Qué significa tener un bloqueo y los tipos que existen.',
  cupo: 'Cómo se calcula el cupo comprometido y el límite pedido.',
  horarios: 'Por qué necesitás horarios libres para recibir asignaciones.',
  reprogramacion: 'Qué disponibilidad mínima necesitás para recibir pacientes nuevos.',
  inactividad: 'Qué mide y por qué es la métrica más importante.',
};

const HUB_CARDS: SubPanelType[] = ['concept', 'blocks', 'cupo'];

const InfoPanel: React.FC<InfoPanelProps> = ({ type, onClose, onSwitch, onGoToTasaInactividad }) => {
  const isOpen = type !== null;

  // Internal navigation stack
  const [stack, setStack] = useState<Exclude<PanelType, null>[]>([]);

  // Si `type` ya es el panel visible (lo cambió la navegación interna, que lo refleja en la URL), la pila se conserva.
  useEffect(() => {
    if (type !== null) setStack((s) => (s[s.length - 1] === type ? s : [type]));
  }, [type]);

  const currentView = stack[stack.length - 1] ?? null;
  const canGoBack = stack.length > 1;

  function navigate(next: SubPanelType) {
    setStack((s) => [...s, next]);
    // `type` pasa a ser el panel visible para que la URL lo refleje; el efecto de arriba no resetea la pila.
    onSwitch(next);
  }

  function goBack() {
    const previous = stack[stack.length - 2];
    setStack((s) => s.slice(0, -1));
    if (previous) onSwitch(previous);
  }

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 z-[9998] transition-opacity duration-200 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={`fixed right-0 top-0 h-full w-[440px] max-w-full bg-white shadow-2xl z-[9999] flex flex-col transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {canGoBack && (
              <button
                type="button"
                onClick={goBack}
                className="text-[#665090] hover:text-[#4A3D73] transition-colors shrink-0"
                aria-label="Volver"
              >
                <ChevronLeft size={20} />
              </button>
            )}
            <span className="text-sm font-semibold text-[#2D2049] leading-snug truncate">
              {currentView === 'hub'
                ? 'Repasar conceptos'
                : currentView && currentView in PANEL_TITLES
                ? PANEL_TITLES[currentView as SubPanelType]
                : ''}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
            aria-label="Cerrar panel"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">

          {currentView === 'hub' && (
            <div className="flex flex-col gap-3">
              {HUB_CARDS.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => navigate(key)}
                  className="w-full text-left bg-white border border-gray-100 rounded-2xl px-5 py-4 flex items-center justify-between gap-4 hover:border-[#C4B5FD] hover:shadow-sm transition-all"
                >
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-[#2D2049]">
                      {PANEL_TITLES[key]}
                    </span>
                    <span className="text-xs text-gray-400 leading-snug">
                      {PANEL_DESCRIPTIONS[key]}
                    </span>
                  </div>
                  <ChevronRight size={18} className="text-[#665090] shrink-0" />
                </button>
              ))}
            </div>
          )}

          {currentView === 'concept' && (
            <div className="flex flex-col gap-6">
              <p className="text-sm text-gray-600 leading-relaxed">
                Cada vez que empezás a atender a un paciente con un tipo de terapia, se abre un
                proceso terapéutico. Es la relación terapéutica entre vos y ese paciente — la
                combinación de psicóloga + paciente + tipo de terapia (individual, pareja,
                familia, etc).
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Un proceso que pasa a inactivo puede ser reactivado: si el paciente retoma las
                sesiones, el proceso vuelve a estado activo automáticamente.
              </p>
              <div className="flex flex-col gap-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                  Estados de un proceso
                </p>
                <div className="flex flex-col divide-y divide-gray-100">
                  {PROCESS_STATES.map((state) => (
                    <div
                      key={state.label}
                      className="flex items-start gap-4 py-3.5 first:pt-0 last:pb-0"
                    >
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap shrink-0 w-36 text-center ${state.color}`}
                      >
                        {state.label}
                      </span>
                      <p className="text-sm text-gray-600 leading-relaxed">{state.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {currentView === 'blocks' && (
            <div className="flex flex-col gap-6">
              <p className="text-sm text-gray-600 leading-relaxed">
                Llamamos bloqueo a la decisión de dejar de asignar nuevos consultantes durante
                determinada cantidad de tiempo. Esto no afecta de ninguna forma a los procesos
                que ya atendés de manera activa — simplemente, durante el período de bloqueo, no
                recibís procesos nuevos.
              </p>
              <div className="flex flex-col divide-y divide-gray-100">
                {BLOCK_TYPES.map((block) => (
                  <div
                    key={block.name}
                    className="py-4 flex flex-col gap-1.5 first:pt-0 last:pb-0"
                  >
                    <span className="text-sm font-semibold text-[#4A3D73]">{block.name}</span>
                    <span className="text-sm text-gray-500 leading-relaxed">
                      {block.description}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {currentView === 'cupo' && (
            <div className="flex flex-col gap-8">

              {/* Cupo comprometido */}
              <section className="flex flex-col gap-4">
                <h3 className="text-sm font-bold text-[#2D2049]">Cupo comprometido</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Es la suma de tus{' '}
                  <button type="button" onClick={() => onSwitch('concept')} className="underline decoration-dotted underline-offset-2 text-[#665090] font-medium hover:text-[#4A3D73] transition-colors">procesos activos</button>
                  {' '}más los pacientes que ya agendaron su primera sesión pero todavía no la
                  tuvieron (los que están en estado{' '}
                  <button type="button" onClick={() => onSwitch('concept')} className="underline decoration-dotted underline-offset-2 text-[#665090] font-medium hover:text-[#4A3D73] transition-colors">Nuevo</button>
                  ).
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Reservamos ese lugar desde el momento en que un paciente agenda con vos, aunque
                  todavía no hayan tenido su primera sesión. Así evitamos que te lleguen más
                  pacientes de los que esperás.
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  También, si un paciente inactivo vuelve a tener una sesión con vos y se
                  reactiva, vuelve a ocupar un lugar en tu cupo comprometido.
                </p>
                <div className="bg-[#F3EFFF] rounded-xl px-4 py-3">
                  <p className="text-xs text-[#665090] leading-relaxed">
                    Es la cifra que usa el sistema para decidir si puede asignarte alguien nuevo.
                  </p>
                </div>
              </section>

              <div className="border-t border-gray-100" />

              {/* Límite pedido */}
              <section className="flex flex-col gap-4">
                <h3 className="text-sm font-bold text-[#2D2049]">Límite pedido por vos</h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Es la cantidad máxima de procesos activos que declaraste que podés atender. Por
                  default, el máximo que asignamos desde Psi es{' '}
                  <strong>40 procesos</strong> — ese tope aplica si tu performance de inactividad
                  es excelente de forma sostenida.
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Podés ajustar este número cuando quieras desde tu perfil. El sistema{' '}
                  <strong>nunca te va a asignar por encima de lo que pediste</strong>.
                </p>
                <div className="bg-[#F3EFFF] rounded-xl px-4 py-3">
                  <p className="text-sm text-[#4A3D73] leading-relaxed">
                    <strong>Excepción:</strong> si un ex paciente tuyo quiere retomar, lo
                    asignamos aunque hayas llegado al límite. Es nuestra política garantizar que
                    esas reactivaciones siempre tengan lugar.
                  </p>
                </div>
              </section>

            </div>
          )}

          {currentView === 'inactividad' && (
            <div className="flex flex-col gap-6">
              <p className="text-sm text-gray-600 leading-relaxed">
                La tasa de inactividad mide qué porcentaje de tus procesos activos pasan a
                inactivo cada mes. Un proceso pasa a inactivo cuando transcurren{' '}
                <strong>30 días sin que haya una sesión</strong>.
              </p>
              <div className="flex flex-col gap-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                  Por qué importa tanto
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Es la métrica que más influye en cuántos pacientes nuevos podemos asignarte.
                  El sistema la revisa mes a mes y ajusta tu límite de asignaciones en base a
                  ella.
                </p>
              </div>
              <div className="bg-[#ECFFD5] rounded-xl px-4 py-3">
                <p className="text-sm font-semibold text-[#2D2049] leading-relaxed">
                  La condición base: mantenerla por debajo del <strong>10% mensual</strong> de
                  forma sostenida.
                </p>
              </div>
              <button
                type="button"
                onClick={onGoToTasaInactividad}
                className="w-full text-left bg-white border border-[#C4B5FD] rounded-2xl px-5 py-4 flex items-center justify-between gap-4 hover:shadow-sm transition-all"
              >
                <span className="text-sm font-semibold text-[#2D2049]">Ver mi tasa de inactividad detallada →</span>
              </button>
            </div>
          )}

          {currentView === 'horarios' && (
            <div className="flex flex-col gap-6">
              <p className="text-sm text-gray-600 leading-relaxed">
                Para que el sistema pueda hacerte un match con un paciente nuevo, necesitás tener
                horarios libres en tu agenda compatibles con la disponibilidad de ese paciente.
                Sin horarios abiertos, <strong>no habrá asignaciones aunque tus métricas estén
                perfectas</strong>.
              </p>
              <div className="flex flex-col gap-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                  Cómo habilitarlos
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Desde tu calendario, activá la disponibilidad para los días y franjas horarias
                  en que estés libre para recibir nuevos pacientes. Cuantos más horarios tengas
                  habilitados, más posibilidades de que un paciente nuevo coincida con vos.
                </p>
              </div>
              <div className="bg-[#F3EFFF] rounded-xl px-4 py-3">
                <p className="text-xs text-[#665090] leading-relaxed">
                  El sistema cruza tu disponibilidad con la del paciente en tiempo real al momento
                  del match.
                </p>
              </div>
            </div>
          )}

          {currentView === 'reprogramacion' && (
            <div className="flex flex-col gap-6">
              <p className="text-sm text-gray-600 leading-relaxed">
                Para poder recibir nuevas asignaciones, el sistema requiere que tengas al menos{' '}
                <strong>4 horas libres en tu agenda</strong> disponibles para que tus pacientes
                actuales puedan reprogramar sus sesiones cuando lo necesiten.
              </p>
              <div className="flex flex-col gap-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">
                  Por qué es necesario
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Si tus pacientes no tienen dónde reprogramar, aumenta el riesgo de que sus
                  procesos pasen a inactivo. Sin esta disponibilidad mínima, el sistema interpreta
                  que estás al límite de tu capacidad y puede pausar las nuevas asignaciones,
                  aunque tus métricas estén bien.
                </p>
              </div>
              <div className="bg-[#F3EFFF] rounded-xl px-4 py-3">
                <p className="text-xs text-[#665090] leading-relaxed">
                  Podés configurarlo desde tu calendario habilitando horarios de reprogramación
                  adicionales.
                </p>
              </div>
            </div>
          )}

        </div>
      </div>
    </>,
    document.body
  );
};

// --- Clickable concept link ---

interface ConceptLinkProps {
  children: React.ReactNode;
  onClick: () => void;
}

const ConceptLink: React.FC<ConceptLinkProps> = ({ children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="underline decoration-dotted underline-offset-2 text-[#665090] font-medium cursor-pointer hover:text-[#4A3D73] transition-colors"
  >
    {children}
  </button>
);

// --- Main component ---

export const MetricsAsignacionTab: React.FC<Props> = ({
  metrics,
  loading,
  onGoToTasaInactividad,
  blockades,
}) => {
  const [openPanel, setOpenPanel] = useState<PanelType>(null);
  useMetricsPanelUrl<Exclude<PanelType, null>>('asignacion', openPanel, setOpenPanel);
  const [inactividadData, setInactividadData] = useState<InactivityRateData | null>(null);
  const [inactividadLoading, setInactividadLoading] = useState(true);
  const [detailData, setDetailData] = useState<InactivityDetailData | null>(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [snapshotData, setSnapshotData] = useState<SnapshotData | null>(null);
  const [limitePedido, setLimitePedido] = useState<number | null>(null);
  const { psychologistId } = useAuth();
  const { psychologist } = usePsychologistContext();
  const isPendingDischarged = psychologist?.statusId === PENDING_DISCHARGED_STATUS_ID;
  const useDetailRateFallback = isPendingDischarged;
  const navigate = useNavigate();
  // NZ, u O que viene de NZ: único canal es administración/NZ (WhatsApp de NZ, no el de CX de F/N/resto de O).
  const { email: contactEmail } = useContactChannel();

  // Mes de reporte = mes de fecha_sync (ej: junio). Se muestra en el banner.
  // Mes evaluado = mes anterior al de reporte. Es lo que se pasa al webhook de inactividad,
  // igual que hace MetricsInactividadTab con prevMonthYYYYMM(selectedMonth).
  const syncDate = snapshotData?.fecha_sync ? new Date(snapshotData.fecha_sync) : null;
  const syncDayLabel = syncDate
    ? syncDate.toLocaleDateString('es-AR', { day: 'numeric', month: 'long' })
    : null;
  // pending_discharged siempre usa el mes real, sin importar snapshotData: fetchSnapshot no
  // filtra por status, así que puede devolver una fila vieja de cuando la psico todavía estaba
  // activa (de antes de pasar a pending_discharged) — el Scheduler ya no le genera filas nuevas
  // porque queda fuera de su roster. Si dejáramos que syncDate gane acá, currentMonthName e
  // inactivityFetchMonth quedarían pegados a esa fila vieja en vez de reflejar el mes real.
  const reportMonthDate = isPendingDischarged ? new Date() : syncDate;
  const currentMonthName = reportMonthDate
    ? reportMonthDate.toLocaleDateString('es-AR', { month: 'long' })
    : '';
  const inactivityFetchMonth = reportMonthDate
    ? (() => {
        const d = new Date(reportMonthDate.getFullYear(), reportMonthDate.getMonth() - 1, 1);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      })()
    : null;

  useEffect(() => {
    if (!psychologistId) return;
    fetchSnapshot()
      .then(setSnapshotData)
      .catch(() => setSnapshotData(null));
    type PrefItem = { id: string; psychologist?: { id?: string; process_limit_preferred?: number | null } };
    apiCoreAdapter.psychologistPreferences(psychologistId)
      .then((resp) => {
        const list: PrefItem[] = Array.isArray(resp) ? resp : ((resp as { results: PrefItem[] }).results ?? []);
        const found = list.find(p => p.psychologist?.id === psychologistId) || list[0];
        setLimitePedido(found?.psychologist?.process_limit_preferred ?? null);
      })
      .catch(() => { /* noop */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [psychologistId]);

  useEffect(() => {
    // useDetailRateFallback ya trae la tasa por otro lado (ver más abajo) — pedirle igual a
    // fetchInactivityRate solo suma una llamada que sabemos que va a fallar (503).
    if (!psychologistId || !inactivityFetchMonth || useDetailRateFallback) return;
    setInactividadLoading(true);
    fetchInactivityRate(inactivityFetchMonth)
      .then(setInactividadData)
      .catch(() => setInactividadData(null))
      .finally(() => setInactividadLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [psychologistId, inactivityFetchMonth, useDetailRateFallback]);

  // pending_discharged queda fuera del roster que evalúa
  // fetchInactivityRate — para esos casos la tasa sale del detalle, que sí procesa a
  // cualquier psicóloga sin importar ranking (mismo cálculo, ver rateFromDetail).
  useEffect(() => {
    if (!psychologistId || !inactivityFetchMonth || !useDetailRateFallback) return;
    const [year, month] = inactivityFetchMonth.split('-');
    setDetailLoading(true);
    fetchInactivityDetail(month, year)
      .then(setDetailData)
      .catch(() => setDetailData(null))
      .finally(() => setDetailLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [psychologistId, inactivityFetchMonth, useDetailRateFallback]);

  const effectiveRate: number | null = useDetailRateFallback
    ? (detailData ? rateFromDetail(detailData) : null)
    : (inactividadData ? inactividadData.deregistration_rate : null);
  const rateLoading = useDetailRateFallback ? detailLoading : inactividadLoading;

  const tasaPct = effectiveRate !== null
    ? (effectiveRate * 100).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : null;
  const tasaEsBuena = effectiveRate !== null && effectiveRate * 100 < 10;

  const cupo = metrics?.committed_process_count ?? null;

  // El cupo y el límite pueden venir en null: el webhook los omite para psicólogas
  // fuera del roster que evalúa. Sin los dos números no se puede calcular el variant,
  // así que se trata igual que "no hay snapshot" en vez de asumir 0 (que daría un
  // variant real y un banner equivocado).
  const rawVariant =
    snapshotData != null &&
    snapshotData.cupo_snapshot != null &&
    snapshotData.process_limit_snapshot != null
      ? getVariant(snapshotData.cupo_snapshot, snapshotData.process_limit_snapshot)
      : null;
  // A las psicos en pending_discharged no les avisamos que es definitivo: siempre les mostramos
  // el mensaje de "no asignaciones" con la ventana más larga (9 a 12 meses, mismo copy que C3),
  // sin importar cómo dé su cupo/proceso — no van a volver a entrar en el ranking de asignación.
  // No depende de snapshotData porque, igual que fetchInactivityRate, fetchSnapshot tampoco
  // encuentra a estas psicos.
  const variant: Variant | null = isPendingDischarged ? 'C3' : rawVariant;
  const variantStyle = variant ? VARIANT_BASE_STYLES[variant] : null;
  // El banner normal exige snapshotData (cupo/proceso) para calcular el variant real; para
  // pending_discharged el variant ya está forzado y no depende de ese dato.
  const showBanner = isPendingDischarged || snapshotData !== null;

  const asignablesEsteMes =
    snapshotData != null &&
    snapshotData.cupo_snapshot != null &&
    snapshotData.process_limit_snapshot != null
      ? Math.min(
          Math.max(snapshotData.process_limit_snapshot - snapshotData.cupo_snapshot, 0),
          5,
        )
      : null;

  const activeBlockades = blockades.filter((b) => b.active);
  const topeBlockade = activeBlockades.find((b) => b.reason?.name === TOPE_BLOCKADE_NAME);
  const internalBlockades = activeBlockades.filter((b) => INTERNAL_BLOCKADE_NAMES.includes(b.reason?.name ?? ''));
  const hasLimitePeriodo = internalBlockades.some((b) => b.reason?.name === LIMITE_PERIODO_BLOCKADE_NAME);
  const hasDemasiados = internalBlockades.some((b) => b.reason?.name === DEMASIADOS_PACIENTES_BLOCKADE_NAME);
  // Solo bloqueos accionables (excluye tope propio e internos)
  const otherBlockades = activeBlockades.filter(
    (b) => b.reason?.name !== TOPE_BLOCKADE_NAME && !INTERNAL_BLOCKADE_NAMES.includes(b.reason?.name ?? ''),
  );
  const onlyInternalBlockades = !topeBlockade && otherBlockades.length === 0 && internalBlockades.length > 0;
  // Cupo exactamente igual al límite: no queda lugar para asignar. Solo ese caso muestra el mensaje
  // de "no podemos asignarte este mes" con la tasa; un bloqueo interno con cupo por debajo del
  // límite (p. ej. el tope de 5 nuevos por mes) conserva su mensaje propio.
  const atLimit =
    snapshotData != null &&
    snapshotData.cupo_snapshot != null &&
    snapshotData.process_limit_snapshot != null &&
    snapshotData.cupo_snapshot === snapshotData.process_limit_snapshot;
  const showAtLimitMessage = variant === 'A' && atLimit && otherBlockades.length === 0;
  // Se compara el valor redondeado, el mismo que ve la psicóloga en pantalla.
  const tasaAltaAtLimit = effectiveRate !== null && Math.round(effectiveRate * 100) > 10;

  return (
    <>
      <InfoPanel
        type={openPanel}
        onClose={() => setOpenPanel(null)}
        onSwitch={(t) => setOpenPanel(t)}
        onGoToTasaInactividad={onGoToTasaInactividad}
      />

      <div className="flex flex-col gap-8">

        {/* 1. Banner A/B/C */}
        {variantStyle && variant && showBanner && rateLoading && (
          <div className="flex flex-col gap-2">
            <div className="h-4 w-48 bg-gray-200 rounded animate-pulse" />
            <div className="rounded-2xl px-6 py-6 bg-gray-100 border border-gray-200 flex flex-col gap-3">
              <div className="h-5 w-3/4 bg-gray-200 rounded animate-pulse" />
              <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
              <div className="h-4 w-2/3 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        )}
        {variantStyle && variant && showBanner && !rateLoading && (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-semibold text-[#4A3D73] uppercase tracking-wide">
              Tendencia sobre asignaciones nuevas
            </p>
            {(() => {
              const canReceivePatients = (asignablesEsteMes ?? 0) > 0 && !topeBlockade;
              const showYellow = variant === 'A' && !canReceivePatients && effectiveRate !== null && effectiveRate * 100 >= 11;
              return (
              <div
                className={`${showYellow ? 'bg-[#FFF8DC] border border-[#FCD34D]' : `${variantStyle.bg} ${variantStyle.border}`} rounded-2xl px-6 py-6 flex flex-col gap-3`}
              >
              <p className="text-lg font-bold text-[#2D2049]">
                {showYellow ? '🟡' : variantStyle.dot}{' '}
                {showYellow
                  ? 'Recordá que lo ideal es mantener la tasa de inactividad por debajo del 10% para cuidar la adherencia de tus procesos'
                  : variant === 'A' && (onlyInternalBlockades || showAtLimitMessage)
                    ? '¡Tus procesos vienen muy bien!'
                    : variantStyle.title}
              </p>

              <p className="text-sm leading-relaxed text-[#2D2049]">
                {showAtLimitMessage && (
                  <>
                    Tu{' '}
                    <ConceptLink onClick={() => setOpenPanel('inactividad')}>
                      tasa de inactividad
                    </ConceptLink>{' '}
                    de {currentMonthName} fue de <strong>{tasaPct ?? '—'}%</strong>.{' '}
                    {tasaAltaAtLimit
                      ? <>Es fundamental mantenerla en <strong>10% o menos</strong> para continuar recibiendo pacientes. No podemos asignarte más pacientes durante este mes, pero si la mantenés baja, ¡se reactivan las asignaciones en los próximos meses!</>
                      : <>¡Buen trabajo! No podemos asignarte más pacientes durante este mes, pero si seguís así, es probable que el mes siguiente se reactiven tus asignaciones.</>
                    }
                    {topeBlockade && (
                      <>
                        <br />
                        <br />
                        Además, tenés un <strong>límite de pacientes pedido por vos</strong>: para que se reactiven las
                        asignaciones, tendrías que sacar ese límite.{' '}
                        <button
                          type="button"
                          onClick={() => navigate(profilePath('attention'))}
                          className="underline text-[#3898EC] hover:opacity-80"
                        >
                          ¡Hacelo acá!
                        </button>
                      </>
                    )}
                  </>
                )}
                {variant === 'A' && onlyInternalBlockades && !showAtLimitMessage && (
                  <>
                    Actualmente no podemos asignarte más pacientes debido a que{' '}
                    {hasLimitePeriodo && hasDemasiados && (
                      <>alcanzaste el <strong>límite de 5 pacientes nuevos</strong> que podemos asignar en el transcurso de un mes y llegaste al <strong>límite de pacientes asignables</strong></>
                    )}
                    {hasLimitePeriodo && !hasDemasiados && (
                      <>alcanzaste el <strong>límite de 5 pacientes nuevos</strong> que podemos asignar en el transcurso de un mes</>
                    )}
                    {hasDemasiados && !hasLimitePeriodo && (
                      <>llegaste al <strong>límite de pacientes asignables</strong></>
                    )}
                    . Cualquier duda, consultanos a{' '}
                    <a href={`mailto:${contactEmail}`} className="underline text-[#3898EC]">
                      {contactEmail}
                    </a>
                    .
                  </>
                )}
                {variant === 'A' && topeBlockade && !showAtLimitMessage && (
                  <>
                    Tu{' '}
                    <ConceptLink onClick={() => setOpenPanel('inactividad')}>
                      tasa de inactividad
                    </ConceptLink>{' '}
                    de {currentMonthName} fue de <strong>{tasaPct ?? '—'}%</strong>{' '}
                    {effectiveRate !== null && effectiveRate * 100 >= 11
                      ? <>— hay margen para mejorarla, lo ideal es mantenerla en <strong>10% o menos</strong>, para mantener la adherencia de los procesos. Además, tenés un</>
                      : <>— ¡muy bien! Tus{' '}<ConceptLink onClick={() => setOpenPanel('concept')}>procesos</ConceptLink>{' '}están en buena forma, pero tenés un</>
                    }{' '}
                    <strong>bloqueo por pedido propio</strong>{' '}
                    activo. Si querés recibir nuevas asignaciones, podés normalizarlo desde tu
                    perfil y asegurarte de tener{' '}
                    <ConceptLink onClick={() => setOpenPanel('horarios')}>
                      horarios disponibles
                    </ConceptLink>
                    .
                  </>
                )}
                {variant === 'A' && !topeBlockade && otherBlockades.length > 0 && (
                  <>
                    Tu{' '}
                    <ConceptLink onClick={() => setOpenPanel('inactividad')}>
                      tasa de inactividad
                    </ConceptLink>{' '}
                    de {currentMonthName} fue de <strong>{tasaPct ?? '—'}%</strong> — tus{' '}
                    <ConceptLink onClick={() => setOpenPanel('concept')}>
                      procesos
                    </ConceptLink>{' '}
                    vienen bien, pero tenés un bloqueo activo de tipo{' '}
                    <strong>
                      &ldquo;{otherBlockades.map((b) => b.reason?.name).filter(Boolean).join(', ')}&rdquo;
                    </strong>{' '}
                    que impide nuevas asignaciones. Normalizalo y asegurate de tener{' '}
                    <ConceptLink onClick={() => setOpenPanel('horarios')}>
                      horarios disponibles
                    </ConceptLink>{' '}
                    para que el sistema pueda asignarte pacientes.
                  </>
                )}
                {variant === 'A' && !topeBlockade && !onlyInternalBlockades && !showAtLimitMessage && otherBlockades.length === 0 && (
                  <>
                    Tu{' '}
                    <ConceptLink onClick={() => setOpenPanel('inactividad')}>
                      tasa de inactividad
                    </ConceptLink>{' '}
                    de {currentMonthName} fue de <strong>{tasaPct ?? '—'}%</strong>.{' '}
                    {effectiveRate !== null && effectiveRate * 100 >= 11
                      ? <>Hay margen para mejorarla — lo ideal es mantenerla en <strong>10% o menos</strong>, para mantener la adherencia de los procesos.</>
                      : <>¡Estás haciendo un gran trabajo manteniendo comprometidos tus{' '}
                          <ConceptLink onClick={() => setOpenPanel('concept')}>procesos</ConceptLink>!</>
                    }
                    <br />
                    <br />
                    Con tu{' '}
                    <ConceptLink onClick={() => setOpenPanel('cupo')}>
                      cupo comprometido
                    </ConceptLink>{' '}
                    de <strong>{cupo} procesos</strong>{' '}
                    <span className="text-[#665090] text-xs">(al {syncDayLabel})</span>
                    {' '}podríamos llegar a asignarte hasta{' '}
                    <strong>{asignablesEsteMes} {asignablesEsteMes === 1 ? 'paciente' : 'pacientes'}</strong>{' '}
                    durante {currentMonthName}.
                    <br />
                    Para que los matches lleguen, asegurate de tener{' '}
                    <ConceptLink onClick={() => setOpenPanel('horarios')}>
                      horarios disponibles
                    </ConceptLink>{' '}
                    y al menos 4{' '}
                    <ConceptLink onClick={() => setOpenPanel('reprogramacion')}>
                      horas de reprogramación
                    </ConceptLink>{' '}
                    para tus pacientes actuales.
                  </>
                )}
                {variant === 'B' && (
                  <>
                    {tasaEsBuena
                      ? <>Venís bien este mes — tu{' '}<ConceptLink onClick={() => setOpenPanel('inactividad')}>tasa de inactividad</ConceptLink>{' '}de {currentMonthName} fue de <strong>{tasaPct}%</strong>. Considerando el histórico, es probable que en los próximos{' '}</>
                      : <>Tu{' '}<ConceptLink onClick={() => setOpenPanel('inactividad')}>tasa de inactividad</ConceptLink>{' '}de {currentMonthName} fue de <strong>{tasaPct ?? '—'}%</strong>. Con este nivel, es probable que en los próximos{' '}</>
                    }
                    <strong>
                    <TooltipProvider>
                      <Tooltip delayDuration={100}>
                        <TooltipTrigger asChild>
                          <span className="underline decoration-dotted underline-offset-2 cursor-help">
                            3 meses
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs leading-relaxed">
                          Este estimado asume que no tenés bloqueos activos ni un límite autoimpuesto de pacientes. Cualquiera de esos factores puede extender el período.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {' '}no recibas{' '}
                    <ConceptLink onClick={() => setOpenPanel('concept')}>
                      pacientes nuevos
                    </ConceptLink>
                    </strong>
                    {tasaEsBuena
                      ? <>. Si mantenés la tasa por debajo del 10% de forma sostenida, el sistema puede volverte a priorizar.</>
                      : <>. Esto puede cambiar: si la tasa baja y se mantiene por debajo del 10% de forma sostenida, el sistema puede volverte a priorizar.</>
                    }
                  </>
                )}
                {(variant === 'C1' || variant === 'C2' || variant === 'C3') && (
                  <>
                    {tasaEsBuena
                      ? <>Venís bien este mes — tu{' '}<ConceptLink onClick={() => setOpenPanel('inactividad')}>tasa de inactividad</ConceptLink>{' '}de {currentMonthName} fue de <strong>{tasaPct}%</strong>. Considerando el histórico, estimamos que en los próximos{' '}</>
                      : <>Tu{' '}<ConceptLink onClick={() => setOpenPanel('inactividad')}>tasa de inactividad</ConceptLink>{' '}de {currentMonthName} fue de <strong>{tasaPct ?? '—'}%</strong>. Con este nivel, estimamos que en los próximos{' '}</>
                    }
                    <strong>
                    <TooltipProvider>
                      <Tooltip delayDuration={100}>
                        <TooltipTrigger asChild>
                          <span className="underline decoration-dotted underline-offset-2 cursor-help">
                            {variant === 'C1' ? '5 meses' : variant === 'C2' ? '7 meses' : '9 a 12 meses'}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs leading-relaxed">
                          Este estimado asume que no tenés bloqueos activos ni un límite autoimpuesto de pacientes. Cualquiera de esos factores puede extender el período.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {' '}no habrá nuevas asignaciones.
                    </strong>{' '}
                    {tasaEsBuena
                      ? <>Mantener la tasa por debajo del 10% de forma consistente es la única forma de revertirlo.</>
                      : <>La única forma de revertirlo es bajar la tasa por debajo del 10% de forma sostenida.</>
                    }
                  </>
                )}
              </p>

              <div className="mt-2 bg-white/60 rounded-xl px-4 py-3 border border-white/80">
                <p className="text-xs font-medium text-[#4A3D73] leading-relaxed">
                  Este mensaje se actualiza una vez al mes, durante la primer semana. Refleja tu situación al inicio de {currentMonthName} — no cambia si recibís pacientes durante el mes.
                </p>
              </div>
            </div>
              );
            })()}
          </div>
        )}

        <hr className="border-2 border-[#2D2049] rounded-full" />

        {/* 2. Stat cards */}
        <div className="flex flex-col gap-3 mt-2">
          <p className="text-sm font-semibold text-[#4A3D73] uppercase tracking-wide">
            Tu panorama HOY
          </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            icon={<span className="text-base">👥</span>}
            value={loading ? '—' : String(metrics?.active_processes ?? 0)}
            label={
              <ConceptLink onClick={() => setOpenPanel('concept')}>
                Procesos activos
              </ConceptLink>
            }
          />
          <MetricCard
            icon={<span className="text-base">🔒</span>}
            value={loading ? '—' : String(cupo ?? 0)}
            label={
              <ConceptLink onClick={() => setOpenPanel('cupo')}>
                Cupo comprometido
              </ConceptLink>
            }
          />
          <MetricCard
            icon={<span className="text-base">📋</span>}
            value={loading ? '—' : String(limitePedido ?? '—')}
            label={
              <ConceptLink onClick={() => setOpenPanel('cupo')}>
                Límite pedido por vos
              </ConceptLink>
            }
            subLabel={
              <button
                type="button"
                onClick={() => navigate(profilePath('attention'))}
                className="text-xs text-[#3898EC] underline text-left hover:opacity-80 w-fit mt-0.5"
              >
                Ajustar mi límite →
              </button>
            }
          />
        </div>
        </div>

        {/* 3. Tasa de inactividad */}
        <div className={`rounded-2xl px-6 py-6 flex flex-col gap-4 border ${(() => {
          const pct = effectiveRate !== null ? effectiveRate * 100 : 0;
          if (loading || effectiveRate === null) return 'bg-white border-gray-200';
          if (pct <= 10) return 'bg-green-50 border-green-200';
          if (pct <= 15) return 'bg-yellow-50 border-yellow-200';
          if (pct <= 20) return 'bg-orange-50 border-orange-200';
          return 'bg-red-100 border-red-300';
        })()}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-1.5">
              <p className="text-base font-semibold text-[#4A3D73]">Tu tasa de inactividad</p>
              <InfoTooltip text="Mide cuántos de tus procesos activos pasaron a inactivo en un determinado período." placement="top" />
            </div>
            <button
              type="button"
              onClick={onGoToTasaInactividad}
              className="text-xs text-[#3898EC] underline hover:opacity-80 whitespace-nowrap shrink-0"
            >
              Más detalles de tu tasa acá →
            </button>
          </div>

          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold text-[#2D2049]">
              {loading || effectiveRate === null ? '—' : `${tasaPct}%`}
            </span>
            <span className="text-sm text-gray-500">tasa de {currentMonthName}</span>
          </div>

          <p className="text-sm text-gray-600 leading-relaxed">
            Para que esté sana, mantenerla debajo del <strong>10% mensual</strong> de forma
            sostenida en el tiempo es lo que más influye en tu límite de asignaciones.
          </p>

        </div>

        {/* 4. Cómo se te asignan pacientes nuevos */}
        <div className="flex flex-col gap-3 mt-2">
          <p className="text-sm font-semibold text-[#4A3D73] uppercase tracking-wide">
            Cómo funciona la asignación
          </p>
        <div className="bg-[#F3EFFF] rounded-2xl px-6 py-6 flex flex-col gap-6">

          <div className="flex flex-col gap-3">
            <p className="text-base font-bold text-[#2D2049]">
              Cómo se te asignan pacientes nuevos
            </p>
            <p className="text-sm text-[#4A3D73] leading-relaxed">
              Cuando un nuevo paciente ingresa a Psi Mammoliti, completa un formulario con su
              motivo de consulta, disponibilidad horaria, y algunas preferencias. Con esa
              información, nuestro sistema hace un match automático.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-xs font-bold uppercase tracking-widest text-[#4A3D73]">
              La base es la tasa de inactividad
            </p>
            <p className="text-sm text-[#4A3D73] leading-relaxed">
              El criterio más importante es que tu{' '}
              <strong>tasa de inactividad se mantenga por debajo del 10%</strong> de forma
              sostenida.
            </p>
            <div className="bg-[#ECFFD5] rounded-xl px-4 py-3">
              <p className="text-sm font-semibold text-[#2D2049] leading-relaxed">
                Esa es la condición base para que el sistema te priorice en nuevas asignaciones.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-xs font-bold uppercase tracking-widest text-[#4A3D73]">
              El sistema también evalúa
            </p>
            <div className="flex flex-wrap gap-2">
              {/* Bloqueos como chip clickeable */}
              <button
                type="button"
                onClick={() => setOpenPanel('blocks')}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-[#E9E0FC] text-[#4A3D73] underline decoration-dotted underline-offset-2 hover:bg-[#DDD3F8] transition-colors cursor-pointer"
              >
                Bloqueos activos, si tenés alguno
              </button>
              {MATCH_CRITERIA.map((item) => (
                <span
                  key={item}
                  className="inline-block px-3 py-1.5 rounded-full text-xs font-medium bg-[#E9E0FC] text-[#4A3D73]"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-[#C4B5FD] rounded-xl px-4 py-4">
            <p className="text-base font-semibold text-[#2D2049] leading-relaxed">
              No hay una fórmula fija — el sistema pondera todo en conjunto y elige a quién
              priorizar en cada momento.
            </p>
          </div>

        </div>
        </div>


        {/* Botón repasar conceptos */}
        <button
          type="button"
          onClick={() => setOpenPanel('hub')}
          className="w-full flex items-center justify-center gap-2 bg-[#4A3D73] hover:bg-[#3a2f5c] text-white text-sm font-semibold py-3.5 rounded-2xl transition-colors"
        >
          Repasar conceptos
          <ChevronRight size={16} />
        </button>

      </div>
    </>
  );
};
